import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import Reveal from "@/components/Reveal";
import GoldSeam from "@/components/GoldSeam";
import { FAQS, TERMS } from "@/data/services";
import { waLink } from "@/lib/site";

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <main className="pt-20" data-testid="faq-page">
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div
          className="absolute -top-32 -left-24 w-[480px] h-[480px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(169,129,47,0.13), transparent 62%)" }}
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="eyebrow mb-6">FAQ &amp; Terms</p>
            <h1
              className="font-display text-ink font-medium leading-[1.06]"
              style={{ fontSize: "clamp(2.4rem, 5.2vw, 4.2rem)" }}
              data-testid="faq-heading"
            >
              The questions people actually ask
            </h1>
            <p className="mt-8 text-inkdim leading-relaxed max-w-xl text-[15px] md:text-base">
              Plainly answered, before you write. If yours isn't here, ask it — the
              answer will come from Rajdeep, not a template.
            </p>
            <GoldSeam variant="horizontal" className="mt-10 w-56 h-10 opacity-70" delay={0.2} />
          </Reveal>
          <Reveal delay={0.12}>
            <div className="relative border border-[rgba(169,129,47,0.26)] p-3">
              <span className="absolute -top-px -right-px w-5 h-5 border-t border-r border-gold" aria-hidden="true" />
              <img
                src="/images/faq-still.jpg"
                alt="A pale ceramic cup on a marble ledge beside folded ivory linen, soft leaf shadows on the wall"
                loading="lazy"
                className="w-full h-[260px] md:h-[360px] object-cover"
                data-testid="faq-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-8 md:pb-16" data-testid="faq-list-section">
        <div className="max-w-4xl mx-auto px-6 border-t border-[rgba(169,129,47,0.26)]">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} className="border-b border-[rgba(169,129,47,0.26)]" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-start justify-between gap-6 py-7 text-left group"
                >
                  <span className="font-display text-xl md:text-2xl text-ink group-hover:text-gold transition-colors duration-300">
                    {f.q}
                  </span>
                  <Plus
                    size={18}
                    strokeWidth={1.5}
                    className={`text-gold mt-1.5 shrink-0 transition-transform duration-500 ${isOpen ? "rotate-45" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="pb-8 pr-10 text-inkdim leading-relaxed text-[15px]" data-testid={`faq-answer-${i}`}>
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-20 md:py-28 bg-paper border-t border-[rgba(169,129,47,0.26)]" data-testid="terms-section">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <p className="eyebrow mb-6">Important terms</p>
            <h2 className="font-display text-ink font-medium leading-[1.1] text-3xl sm:text-4xl" data-testid="terms-heading">
              Please read before booking
            </h2>
          </Reveal>
          <div className="mt-12 space-y-9">
            {TERMS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.05}>
                <div className="border-t border-[rgba(169,129,47,0.26)] pt-6" data-testid={`term-item-${i}`}>
                  <h3 className="text-[11px] uppercase tracking-[0.24em] text-gold mb-3">{t.title}</h3>
                  <p className="text-inkdim leading-relaxed text-[15px]">{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14 flex flex-wrap items-center gap-6">
              <Link
                to="/booking"
                data-testid="faq-book-session-button"
                className="bg-deep text-canvas text-xs uppercase tracking-[0.24em] font-semibold px-9 py-4 hover:bg-gold transition-colors duration-300"
              >
                Book a Session
              </Link>
              <Link
                to="/contact"
                data-testid="faq-contact-link"
                className="gold-underline-link text-[11px] uppercase tracking-[0.24em] text-gold"
              >
                Ask something else
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
